import { useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Users, MapPin, Crown, ShieldCheck, User, Truck, Building2, AlertTriangle, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REGIONAL_MANAGER_HERO =
  'https://customer-assets-eiarnc6j.emergentagent.net/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/88q4cnv9_3135.png';

const ROLE_META = {
  manager:     { label: 'Regional Manager', icon: Crown,        color: '#FFD60A' },
  supervisor:  { label: 'Supervisor',       icon: ShieldCheck,  color: '#C9A962' },
  crew_leader: { label: 'Crew Leader',      icon: ShieldCheck,  color: '#a5b4fc' },
  agent:       { label: 'Site Agent',       icon: User,         color: '#94a3b8' },
  site_agent:  { label: 'Site Agent',       icon: User,         color: '#94a3b8' },
  cleaner:     { label: 'Cleaner',          icon: User,         color: '#22d3ee' },
};

// Iter-85 · Position selector tabs. Clicking one filters the roster below.
const POSITION_TABS = [
  { key: 'all',         label: 'All',          match: () => true },
  { key: 'supervisor',  label: 'Supervisors',  match: (m) => m.role === 'supervisor' },
  { key: 'crew_leader', label: 'Crew Leaders', match: (m) => m.role === 'crew_leader' },
  { key: 'agent',       label: 'Site Agents',  match: (m) => m.role === 'agent' || m.role === 'site_agent' },
];

/**
 * RegionalTeamBoardCard
 * ---------------------
 * Compact dashboard card that sits right under the Clock-In section for the
 * Regional Manager. Displays the NECS training-room hero picture as the
 * "board" on the left and a tight staff-roster preview on the right, so
 * the RM lands on the dashboard already seeing:
 *   • Their team roster (supervisors → crew leaders → agents)
 *   • Total headcount + community count in the region
 * Pulls from GET /api/routes/team-view (already used by the hierarchy tab).
 */
export function RegionalTeamBoardCard({ user }) {
  const [entries, setEntries] = useState([]);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/routes/team-view`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        setEntries((data || []).filter((e) => e.region?.region_id && e.region.region_id !== '_unassigned'));
      } catch (_) { /* silent */ }
    })();
  }, []);

  const teamView = useMemo(() => {
    if (!entries.length) return null;
    // Prefer the RM's own region.
    return entries.find((e) => (user?.region_id && e.region.region_id === user.region_id)
                            || (user?.region && e.region.region_code === user.region))
      || entries[0];
  }, [entries, user?.region_id, user?.region]);

  const roster = useMemo(() => {
    if (!teamView) return [];
    return (teamView.staff || []).filter((s) => s.role !== 'admin' && s.role !== 'company_manager');
  }, [teamView]);

  const stats = useMemo(() => {
    const props = teamView?.properties || [];
    const s = teamView?.staff || [];
    const totals = teamView?.monthly_totals || {};
    return {
      cities: new Set(props.map((p) => (p.city || '').trim()).filter(Boolean)).size,
      properties: props.length,
      vehicles: totals.fleet_count ?? 0,
      supervisors: s.filter((x) => x.role === 'supervisor').length,
      leaders:     s.filter((x) => x.role === 'crew_leader').length,
      agents:      s.filter((x) => x.role === 'agent' || x.role === 'site_agent').length,
      violations:  totals.violations_30d ?? 0,
    };
  }, [teamView]);

  // Iter-85 · Filter roster by the active position tab.
  const filteredRoster = useMemo(() => {
    const tab = POSITION_TABS.find((t) => t.key === activeTab) || POSITION_TABS[0];
    return roster.filter(tab.match);
  }, [roster, activeTab]);

  const tabCount = (key) => {
    const t = POSITION_TABS.find((x) => x.key === key);
    return t ? roster.filter(t.match).length : 0;
  };

  // Iter-86 · Roster CSV export — honors the active position filter.
  const downloadCsv = () => {
    if (!teamView) return;
    const rows = filteredRoster;
    if (!rows.length) return;
    const regionCode = teamView.region?.region_code || 'REGION';
    const date = new Date().toISOString().slice(0, 10);  // YYYY-MM-DD
    const filename = `NECS_Roster_${regionCode}_${date}.csv`;

    const escape = (v) => {
      if (v == null) return '';
      const s = String(v);
      // Excel-safe: wrap anything with a comma / quote / newline in double-quotes,
      // and double any inner double-quotes.
      return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
    };
    const header = ['Name', 'Role', 'Badge ID', 'Region', 'Status', 'Contact Number'];
    const body = rows.map((m) => [
      m.name || '',
      (ROLE_META[m.role] || {}).label || m.role || '',
      m.badge_number || '',
      regionCode,
      // Status: derive from active/clock_in flags; default to Active.
      m.active === false ? 'Inactive' : (m.on_shift ? 'On Shift' : 'Active'),
      m.phone || m.contact_number || '',
    ].map(escape).join(','));
    const csv = [header.map(escape).join(','), ...body].join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card
      className="p-4 mb-6 border-2 bg-zinc-900"
      style={{ borderColor: '#C9A962' }}
      data-testid="regional-team-board-card"
    >
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* LEFT — the board picture */}
        <div className="md:col-span-2 relative rounded-lg overflow-hidden border border-zinc-800">
          <img
            src={REGIONAL_MANAGER_HERO}
            alt="NECS regional leadership meeting"
            className="w-full h-full object-cover"
            style={{ minHeight: 220, maxHeight: 320 }}
            data-testid="regional-team-board-image"
          />
          <div
            className="absolute inset-x-0 bottom-0 p-2"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)' }}
          >
            <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: '#C9A962' }}>
              Regional Command
            </p>
            <p
              className="text-white text-sm font-bold truncate"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {teamView?.region?.region_code || user?.region || 'R07'} · {teamView?.region?.name || 'Central Dallas'}
            </p>
          </div>
        </div>

        {/* RIGHT — team roster with position selector tabs */}
        <div className="md:col-span-3">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3
              className="text-white font-bold flex items-center gap-2"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              <Users className="h-5 w-5" style={{ color: '#C9A962' }} />
              TEAM ROSTER · {filteredRoster.length} / {roster.length} MEMBERS
            </h3>
            <Badge className="bg-[#FFD60A] text-black text-[10px]" data-testid="regional-team-board-count">
              {stats.properties} properties · {stats.cities} cities
            </Badge>
          </div>

          {/* Iter-85 · Position selector tabs + iter-86 CSV export.
              Clicking a tab filters the roster; the CSV button honors
              whatever filter is currently active. */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="regional-team-position-tabs">
            {POSITION_TABS.map((tab) => {
              const isActive = tab.key === activeTab;
              const count = tabCount(tab.key);
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className="px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition"
                  style={{
                    backgroundColor: isActive ? '#C9A962' : 'rgba(201,169,98,0.12)',
                    color: isActive ? '#1a1a1a' : '#C9A962',
                    border: `1px solid ${isActive ? '#C9A962' : 'rgba(201,169,98,0.4)'}`,
                  }}
                  data-testid={`regional-team-tab-${tab.key}`}
                >
                  {tab.label} · {count}
                </button>
              );
            })}
            <button
              type="button"
              onClick={downloadCsv}
              disabled={filteredRoster.length === 0}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition disabled:opacity-40"
              style={{
                backgroundColor: 'rgba(16,185,129,0.15)',
                color: '#6ee7b7',
                border: '1px solid rgba(16,185,129,0.55)',
              }}
              data-testid="regional-team-csv-export"
              title="Download the currently-filtered roster as a CSV"
            >
              <Download className="h-3 w-3" />
              Download CSV
            </button>
          </div>

          {filteredRoster.length === 0 ? (
            <p className="text-xs text-zinc-400" data-testid="regional-team-empty">
              {roster.length === 0
                ? 'No team members assigned yet in this region.'
                : `No ${(POSITION_TABS.find((t) => t.key === activeTab)?.label || '').toLowerCase()} assigned yet in this region.`}
            </p>
          ) : (
            <div
              className="space-y-1.5 overflow-y-auto pr-1"
              style={{ maxHeight: 240 }}
              data-testid="regional-team-board-roster"
            >
              {filteredRoster.map((m) => {
                const meta = ROLE_META[m.role] || { label: m.role, icon: User, color: '#94a3b8' };
                const Icon = meta.icon;
                return (
                  <div
                    key={m.user_id}
                    className="flex items-center gap-2 p-2 rounded-md bg-zinc-950 border border-zinc-800"
                    data-testid={`regional-team-board-member-${m.user_id}`}
                  >
                    <div
                      className="h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${meta.color}22`, border: `1px solid ${meta.color}80` }}
                    >
                      <Icon className="h-3.5 w-3.5" style={{ color: meta.color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-semibold truncate">{m.name}</p>
                      <p className="text-[10px] text-zinc-500 truncate">
                        {meta.label}{m.badge_number ? ` · #${m.badge_number}` : ''}{m.assigned_city ? ` · ${m.assigned_city}` : ''}
                      </p>
                    </div>
                    {m.assigned_property_name && (
                      <span
                        className="text-[10px] flex items-center gap-1 flex-shrink-0"
                        style={{ color: '#C9A962' }}
                      >
                        <MapPin className="h-3 w-3" />
                        {m.assigned_property_name}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Iter-85 · Monthly-update stat boards. Replaces the compact pill
          strip above the roster (which duplicated info already surfaced in
          the Service History Summary). Six primary metrics for the RM to
          monitor throughout the month at a glance. */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-4 pt-4 border-t border-zinc-800" data-testid="regional-team-monthly-stats">
        <MonthlyStat testid="regional-stat-cities"      icon={<MapPin className="h-4 w-4" />}      label="Cities"      value={stats.cities}      tone="#0ea5e9" />
        <MonthlyStat testid="regional-stat-properties"  icon={<Building2 className="h-4 w-4" />}   label="Properties"  value={stats.properties}  tone="#22c55e" />
        <MonthlyStat testid="regional-stat-vehicles"    icon={<Truck className="h-4 w-4" />}       label="Vehicles"    value={stats.vehicles}    tone="#a855f7" />
        <MonthlyStat testid="regional-stat-supervisors" icon={<ShieldCheck className="h-4 w-4" />} label="Supervisors" value={stats.supervisors} tone="#C9A962" />
        <MonthlyStat testid="regional-stat-crew-leaders" icon={<ShieldCheck className="h-4 w-4" />} label="Crew Leaders" value={stats.leaders} tone="#a5b4fc" />
        <MonthlyStat testid="regional-stat-agents"      icon={<User className="h-4 w-4" />}        label="Site Agents" value={stats.agents}      tone="#94a3b8" />
      </div>
      {stats.violations > 0 && (
        <p className="mt-2 text-[10px] text-amber-400 flex items-center gap-1 justify-end" data-testid="regional-stat-violations">
          <AlertTriangle className="h-3 w-3" /> {stats.violations} violations logged in this region (last 30 days)
        </p>
      )}
    </Card>
  );
}

function MonthlyStat({ testid, icon, label, value, tone }) {
  return (
    <div
      className="rounded-lg p-2.5 border bg-zinc-950 flex flex-col items-center text-center"
      style={{ borderColor: `${tone}55` }}
      data-testid={testid}
    >
      <span className="mb-1" style={{ color: tone }}>{icon}</span>
      <span className="text-2xl font-black text-white leading-none">{value}</span>
      <span className="text-[9px] uppercase tracking-widest text-zinc-500 mt-1">{label}</span>
    </div>
  );
}

export default RegionalTeamBoardCard;
